import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {IOrder, IOrderNote} from "@/model/Order/orderInterfaces";
import responseHandler from "@/plugins/errorHandling";
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
} from '@/constants/pagination';

export interface IEligibleLocker {
  id: number;
  name: string;
  city: string;
}

export interface IEligibleServiceProvider {
  id: number;
  name: string;
}

export interface ILockerCellSize {
  id: number;
  name: string;
}

export interface IEligibleService {
  id: number;
  name: any;
  price: number;
  sale_price: number|null;
  on_sale: boolean;
  mandatory: boolean;
  service_provider_id: number;
  service_provider: {id: number; name: string} | null;
  service_provider_type: string|null;
}

interface OrderState {
  orders: IOrder[];
  order: IOrder|null;
  search: IOrder[];
  errors: IErrors;
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  eligibleLockers: IEligibleLocker[];
  canCreateOfficeOrders: boolean;
  eligibleServices: IEligibleService[];
  availableServices: IEligibleService[];
  lockerCellSizes: ILockerCellSize[];
  eligibleServiceProviders: IEligibleServiceProvider[];
  repeatedOrderIds: number[];
}

const state: OrderState = {
  orders: [],
  order: null,
  search: [],
  errors: {},
  currentPage: PAGINATION_FIRST_PAGE,
  lastPage: PAGINATION_DEFAULT_LAST_PAGE,
  total: PAGINATION_DEFAULT_TOTAL,
  perPage: PAGINATION_DEFAULT_PER_PAGE,
  eligibleLockers: [],
  canCreateOfficeOrders: false,
  eligibleServices: [],
  availableServices: [],
  lockerCellSizes: [],
  eligibleServiceProviders: [],
  repeatedOrderIds: [],
};

const getters: GetterTree<OrderState, RootState> = {
  orders: (state): IOrder[] => state.orders,
  order: (state): IOrder|null => state.order,
  search: (state): IOrder[] => state.search,
  errors: (state) => state.errors,
  currentPage: (state): number => state.currentPage,
  lastPage: (state): number => state.lastPage,
  total: (state): number => state.total,
  perPage: (state): number => state.perPage,
  eligibleLockers: (state): IEligibleLocker[] => state.eligibleLockers,
  canCreateOfficeOrders: (state): boolean => state.canCreateOfficeOrders,
  eligibleServices: (state): IEligibleService[] => state.eligibleServices,
  availableServices: (state): IEligibleService[] => state.availableServices,
  lockerCellSizes: (state): ILockerCellSize[] => state.lockerCellSizes,
  eligibleServiceProviders: (state): IEligibleServiceProvider[] => state.eligibleServiceProviders,
  repeatedOrderIds: (state): number[] => state.repeatedOrderIds,
};

const actions: ActionTree<OrderState, RootState> = {
  createPackageDelivery: ({ commit }, {userId, lockerId, cellSizeId, openRemote}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/create/packageDelivery',
      method: 'POST',
      data: {userId, lockerId, cellSizeId, openRemote}
    }),
    commit,
    'orderCreated'
  ),

  collectByAdministrator: ({ commit}, {orderId, collect_reason, openCell, closeOrder}): any => responseHandler.handle(
      () => axios({
          url: settings.endpoint + 'orders/' + orderId + '/collectByAdministrator',
        // probably should be a post better
          method: 'PUT',
          data: { collect_reason, openCell, closeOrder }
      }),
      commit,
      'orderCollectedByAdministrator'
  ),

  deliverByAdministrator: ({ commit}, {orderId, reason, openCell}): any => responseHandler.handle(
      () => axios({
          url: settings.endpoint + 'orders/' + orderId + '/deliver',
        // probably should be a post better
          method: 'PUT',
          data: { reason, openCell }
      }),
      commit,
      'orderDeliveredByAdministrator'
  ),

  fetchOrders:  ({ commit }, filters: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/',
      params: {
        ...filters,
      },
    }),
    commit,
    'ordersLoaded'
  ),

  clearOrders: ({ commit }) => {
    commit('clearOrders');
  },

  fetchOrder: ({ commit }, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId,
    }),
    commit,
    'orderLoaded'
  ),

  finalizeOrder: ({ commit}, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/finalize',
      method: 'PUT'
    }),
    commit,
    'orderLoaded'
  ),
  confirmProfessionalService: ({ commit}, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/confirmProfessionalService',
      method: 'PUT'
    }),
    commit,
    'orderLoaded'
  ),

  updateOrderProfessionalServiceDateTime: ({ commit}, {orderId, newDateTime}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/updateOrderProfessionalServiceDateTime',
      method: 'PUT',
      data: { newDateTime }
    }),
    commit,
    'orderLoaded'
  ),
  deleteOrder: ({commit}, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId,
      method: 'DELETE'
   }),
   commit,
   'orderLoaded'
  ),
  confirmOrder: ({ commit }, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/confirm',
      method: 'PUT',
    }),
    commit,
    'orderLoaded'
  ),
  requestPickupOrder: ({ commit }, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/requestPickup',
      method: 'PUT',
    }),
    commit,
    'orderLoaded'
  ),
  updateOrderStatus: ({ commit }, {orderId, status, payment_status}: {orderId: number; status?: string|null; payment_status?: string|null}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/status',
      method: 'PATCH',
      data: {status, payment_status},
    }),
    commit,
    'orderLoaded'
  ),
  resendConfirmation: ({ commit }, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/resendConfirmation',
      method: 'POST',
    }),
    commit,
    'orderLoaded'
  ),
  serviceProcessed: ({ commit }, {orderId, orderServiceId, processed}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/services/' + orderServiceId + '/processed',
      method: processed ? 'PUT' : 'DELETE',
    }),
    commit,
    'orderLoaded',
  ),
  assembleOrder: ({commit}, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/assemble',
      method: 'PUT',
    }),
    commit,
    'orderLoaded',
  ),
  assignNewCell: ({commit}, {orderId, lockerCellSizeId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/assignNewCell',
      method: 'POST',
      data: {lockerCellSizeId}
    }),
    commit,
    'orderLoaded',
  ),
  releaseCell: ({commit}, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/releaseCell',
      method: 'PUT',
    }),
    commit,
    'orderLoaded',
  ),
  assignNewCode: ({commit}, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/assignNewCode',
      method: 'PUT',
    }),
    commit,
    'orderLoaded',
  ),
  payOrder: ({commit}, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/pay',
      method: 'PUT',
    }),
    commit,
    'orderLoaded',
  ),
  unPayOrder: ({commit}, orderId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/unPay',
      method: 'PUT',
    }),
    commit,
    'orderLoaded',
  ),
  silenceOrder: ({commit}, {orderId, reason}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/silence',
      params: {
        reason
      },
      method: 'POST',
    }),
    commit,
    'orderLoaded',
  ),
  callDevEvent: ({commit}, {orderId, eventName}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/devEvent/' + eventName,
      method: 'POST',
    }),
    commit,
    'orderLoaded'
  ),
  searchOrders: ({commit}, search): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/search',
      method: 'GET',
      params: {search},
    }),
    commit,
    'orderSearchLoaded',
  ),
  clearSearch: ({commit}): any => commit('orderSearchLoaded', []),

  addNote: ({commit}, {orderId, comment}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/notes',
      method: 'POST',
      data: {comment},
    }),
    commit,
    'noteAdded',
  ),

  updateOrderService: ({commit}, {orderId, orderServiceId, quantity, item_price, notes}: {orderId: number; orderServiceId: number; quantity?: number; item_price?: number; notes?: string}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/services/' + orderServiceId,
      method: 'PATCH',
      data: {quantity, item_price, notes},
    }),
    commit,
    'orderLoaded',
  ),

  deleteOrderService: ({commit}, {orderId, orderServiceId}: {orderId: number; orderServiceId: number}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/services/' + orderServiceId,
      method: 'DELETE',
    }),
    commit,
    'orderLoaded',
  ),

  fetchOrderAvailableServices: ({commit}, orderId: number): any => responseHandler.handle(
    () => axios({ url: settings.endpoint + 'orders/' + orderId + '/services/available' }),
    commit,
    'availableServicesLoaded',
  ),

  addOrderService: ({commit}, {orderId, serviceId, quantity, price, customName, lockerCellSizeId}: {orderId: number; serviceId: number; quantity: number; price?: number; customName?: string; lockerCellSizeId?: number|null}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/services',
      method: 'POST',
      data: {service_id: serviceId, quantity, price, custom_name: customName, locker_cell_size_id: lockerCellSizeId},
    }),
    commit,
    'orderLoaded',
  ),

  fetchEligibleLockers: ({commit}): any => responseHandler.handle(
    () => axios({ url: settings.endpoint + 'orders/create/eligible-lockers' }),
    commit,
    'eligibleLockersLoaded',
  ),

  fetchEligibleServices: ({commit}, lockerId?: number|null): any => responseHandler.handle(
    () => axios({ url: settings.endpoint + 'orders/create/eligible-services', params: {lockerId} }),
    commit,
    'eligibleServicesLoaded',
  ),

  fetchLockerCellSizes: ({commit}, params: {lockerId?: number; orderId?: number}): any => responseHandler.handle(
    () => axios({ url: settings.endpoint + 'orders/create/locker-cell-sizes', params }),
    commit,
    'lockerCellSizesLoaded',
  ),

  fetchEligibleServiceProviders: ({commit}, params: {lockerId?: number; orderId?: number}): any => responseHandler.handle(
    () => axios({ url: settings.endpoint + 'orders/create/eligible-service-providers', params }),
    commit,
    'eligibleServiceProvidersLoaded',
  ),

  createManualOrder: ({commit}, {userId, lockerId, addressId, initialStatus, serviceProviderId, services, genericItems, note, promoCode}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/',
      method: 'POST',
      data: {userId, lockerId, addressId, initialStatus, serviceProviderId, services, generic_items: genericItems || [], note, promoCode},
    }),
    commit,
    'orderCreated',
  ),

  repeatOrder: ({commit}, {orderId, first_date, frequency, cnt}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/repeat',
      method: 'POST',
      data: {first_date, frequency, cnt},
    }),
    commit,
    'orderRepeated',
  ),

  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  }

};

const mutations: MutationTree<OrderState> = {
  orderCreated(state: OrderState, payload) {
    state.order = payload;
  },

  orderRepeated(state: OrderState, payload) {
    state.repeatedOrderIds = payload.order_ids || [];
  },

  orderCollectedByAdministrator(state, payload) {
     state.order = payload;
  },

  orderDeliveredByAdministrator(state, payload) {
     state.order = payload;
  },

  orderLoaded(state, payload: IOrder) {
    state.order = payload;
  },

  ordersLoaded(state, payload: any) {
    if (!payload) {
      state.orders = [];
      return;
    }
    state.orders = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    state.currentPage = payload.current_page || PAGINATION_FIRST_PAGE;
    state.lastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.total = payload.total || PAGINATION_DEFAULT_TOTAL;
    state.perPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
  },

  clearOrders(state) {
    state.orders = [];
    state.currentPage = PAGINATION_FIRST_PAGE;
    state.lastPage = PAGINATION_DEFAULT_LAST_PAGE;
    state.total = PAGINATION_DEFAULT_TOTAL;
  },

  orderSearchLoaded(state, payload: IOrder[]) {
    state.search = payload;
  },

  noteAdded(state, payload: IOrderNote) {
    if (state.order) {
      state.order.notes = [payload, ...state.order.notes];
    }
  },

  errors(state, payload: IErrors) {
    state.errors = payload;
  },

  eligibleLockersLoaded(state, payload: {lockers: IEligibleLocker[]; canCreateOfficeOrders: boolean}) {
    state.eligibleLockers = payload && Array.isArray(payload.lockers) ? payload.lockers : [];
    state.canCreateOfficeOrders = !!(payload && payload.canCreateOfficeOrders);
  },

  eligibleServicesLoaded(state, payload: IEligibleService[]) {
    state.eligibleServices = Array.isArray(payload) ? payload : [];
  },

  lockerCellSizesLoaded(state, payload: ILockerCellSize[]) {
    state.lockerCellSizes = Array.isArray(payload) ? payload : [];
  },

  eligibleServiceProvidersLoaded(state, payload: IEligibleServiceProvider[]) {
    state.eligibleServiceProviders = Array.isArray(payload) ? payload : [];
  },

  availableServicesLoaded(state, payload: IEligibleService[]) {
    state.availableServices = Array.isArray(payload) ? payload : [];
  },

};

const namespaced: boolean = true;

export const orderModule: Module<OrderState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
